import { Fragment, useEffect, useState, useContext, useRef } from 'react';
import { Row, Grid, Col, Table, Button, IconButton, toaster, Message, ButtonGroup, Whisper, Tooltip, ButtonToolbar, Loader, Input } from 'rsuite';
import { useApolloClient, gql, useQuery } from '@apollo/client';
import startCase from 'lodash/startCase';
import { ApplicationContext, ResponsiveTable } from 'shared';
import { v4 as uuidv4 } from 'uuid';
import { COLOR, FORMAT, FORMAT_SHORT_DATE, setHeaderTitle } from 'lib/env';
import ClientRepresentativeSignature from 'components/document/ClientRepresentativeSignature';
import { BrowserView, isMobile, MobileView } from 'react-device-detect';
import { queryDocument } from 'gql/documents';
import { useGetToken } from 'lib/auth';
import { useMediaQuery } from 'react-responsive';
import { format, parseISO } from 'date-fns';
import Icon from '@rsuite/icons/lib/Icon';
import { MdAdd, MdCancel, MdCheckCircle, MdDelete, MdDownload } from 'react-icons/md';
import { useViewport } from 'shared/ViewportProvider';
import { useApi } from 'lib';

const { Column, HeaderCell, Cell } = Table;

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const PortalDocumentView = () => {
  const api = useApi();
  const client = useApolloClient();
  const isTabletOrMobile = useMediaQuery({ maxWidth: 1224 });
  const { showError, showMessage } = useContext(ApplicationContext);
  const { state } = useViewport();
  const [document, setDocument] = useState<any>(undefined);
  const [commentVisible, setCommentVisible] = useState(false);
  const [service, setService] = useState<any>({});
  const [documentSigning, setDocumentSigning] = useState(false);
  const tokenIsExpired = false; //(token as any).exp < new Date().getTime() / 1000;
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [tenantId, setTenantId] = useState(undefined);
  const [tenantContactId, setTenantContactId] = useState(undefined);
  const [documentView, setDocumentView] = useState('');
  const idToken = window.location.pathname.split('/')[window.location.pathname.split('/').length - 1];
  const token: any = useGetToken(true);
  const iframeRef: any = useRef();
  const [iframeHeight, setIframeHeight] = useState("0px");
  const [contacts, setContacts] = useState<Array<any>>([{ guid: uuidv4() }]);

  const IS_ASSINIBOINE = token.data.tenant === 'assiniboine' || token.data.tenant === 'greyrock';
  const IS_GREYROCK = token.data.tenant === 'greyrock';
  const IS_BUGABOO = token.data.tenant === 'bugaboo' || token.data.tenant === 'snowpatch';
  const IS_KALADI_KITCHENS = token.data.tenant === 'kaladikitchens';
  const IS_KALADI_PROPERTIES = token.data.tenant === 'kaladiproperties';

  useEffect(() => {
    (async () => {
      if (IS_BUGABOO) {
        const document = await api.get(`/documents/view/${token.data.resource}?access_token=${idToken}`, 'body');
        setDocumentView(document);
      }
    })();
  }, []);

  useEffect(() => {
    setHeaderTitle('Approve Document');
    (async () => {
      setLoading(true);

      if (token) {
        const data = await client.query({ query: queryDocument, variables: { guid: token.data.resource } });
        setDocument(data.data.document);
        setContacts(data.data.document?.clientContacts || [{ guid: uuidv4() }]);

        if (data.data.document && data.data.document.expiryDate && new Date() > new Date(new Date(data.data.document.expiryDate).setHours(23, 59, 59, 59))) {
          setIsExpired(true);
        }
      }

      setLoading(false);
    })();
  }, []);

  const handleUpdateService = async (guid: string, documentGuid: string, status: string) => {
    try {
      const response: any = await client.mutate({
        mutation: gql`
          mutation updateDocumentService(
            $guid: ID!
            $documentGuid: ID!
            $status: String
          ) {
            updateDocumentService(
              guid: $guid
              documentGuid: $documentGuid
              status: $status
            ) {
              success
              code
              message
            }
          }
        `,
        variables: { guid, documentGuid, status }
      });

      const services = document.services.map((s: any) => {
        const service = Object.assign({}, s);
        service.status = service.guid === guid ? status : service.status;
        return service;
      });
      setDocument({ ...document, services });
      toaster.push(
        <Message type="success" showIcon closable>{status === 'confirmed' ? 'Service confirmed' : 'Service declined'}</Message>
      );
    } catch (err) {
      showError(err);
    }
  };

  const handleSubmitComment = async () => {
    try {
      const response: any = await client.mutate({
        mutation: gql`
          mutation createComment(
            $childGuid: ID!
            $parentGuid: ID!
            $comment: String
          ) {
            createComment(
              childGuid: $childGuid
              parentGuid: $parentGuid
              comment: $comment
              source: "document"
            ) {
              success
              code
              message
            }
          }
        `,
        variables: { childGuid: service.guid, parentGuid: document.guid, comment }
      });

      const services = document.services.map((s: any) => {
        const service = Object.assign({}, s);
        if (service.guid === service.guid) {
          service.comments.push({
            guid: uuidv4(),
            childGuid: service.guid,
            parentGuid: document.guid,
            comment: comment,
            source: "document",
            createdAt: new Date()
          });
        }
        return service;
      });
      setDocument({ ...document, services });
      setService({});
      setCommentVisible(false);
      toaster.push(
        <Message type="success" showIcon closable>{response.data.createComment.message}</Message>
      );
    } catch (err) {
      showError(err);
    }
  };

  const isCompleted = () => {
    return document.services.filter((s: any) => !['header', 'footer'].includes(s.service) && !['confirmed', 'declined'].includes(s.status)).length === 0;
  }

  const handleUpdateContact = async (val: string, el: any, guid?: any) => {
    const key = typeof (el) === 'string' ? el : el.target.name;
    guid = typeof (guid) === 'undefined' ? el.target.dataset.guid : guid;
    setContacts(contacts.map((c: any) => {
      if (c.guid === guid && key) {
        c[key] = val;
      }
      return c;
    }));
  }

  const handleUpdateClientContacts = async () => {
    let response: any | undefined = undefined;

    try {
      setLoading(true);
      // this is the customer signing
      response = await client.mutate({
        mutation: gql`
          mutation clientUpdateDocument($input: UpsertDocumentInput) {
            clientUpdateDocument(
              input: $input
            ) {
              success
              code
              message
            }
          }
        `,
        variables: { input: { guid: document.guid, clientContacts: contacts } }
      });

      toaster.push(
        <Message type="success" showIcon closable>{response.data.clientUpdateDocument.message}</Message>
      );
      setLoading(false);
    } catch (err) {
      showError(response.data.clientUpdateDocument.message);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const excludeApproval = () => {
    return IS_KALADI_KITCHENS || IS_KALADI_PROPERTIES || (IS_GREYROCK && document.entryType.includes('commercial'));
  }

  return (
    <div className="App">
      {(tokenIsExpired || (!loading && !document))
        ? <Fragment>
          <Grid>
            <Row>
              <Col xs={24}>
                <br />
                <Message type="error">This link has expired, please contact our office to receive a new link.</Message>
              </Col>
            </Row>
          </Grid>
        </Fragment>
        : <Fragment>
          {loading
            ? <Loader content="Loading..." />
            : <>
              {document
                ? <Grid fluid={isTabletOrMobile}>
                  <Row>
                    <Col xs={18} md={12}>
                      <MobileView>
                        <h4>{document.customer.displayName}</h4>
                      </MobileView>
                      <BrowserView>
                        <h1>{document.customer.displayName}</h1>
                      </BrowserView>
                      <p><strong>{document.customer.workAddress || document.customer.customerAddress}</strong></p>
                      <p>{format(parseISO(document.startDate), FORMAT.DAY_MONTH_DATE)} to {format(parseISO(document.endDate), FORMAT.DAY_MONTH_DATE)}</p>
                      <br />
                    </Col>
                    <Col xs={4} md={12} className='text-right'>
                      <MobileView>
                        <span>{(state?.fields || []).find((f: any) => f.key === document.entryType)?.title || startCase(document.entryType)}</span>
                      </MobileView>
                      <BrowserView>
                        <h1>{(state?.fields || []).find((f: any) => f.key === document.entryType)?.title || startCase(document.entryType)}</h1>
                      </BrowserView>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={24}>
                      {document.approvalStatus === 'approved'
                        ? <Message type="success">{(state?.fields || []).find((f: any) => f.key === document.entryType)?.title || startCase(document.entryType)} approved &amp; signed.</Message>
                        : <Fragment>
                          {isExpired && <Message type="error">This document acceptance date has expired. Please contact our office for an extension.</Message>}
                          {(!isExpired && !excludeApproval()) && <Message type="info">Confirm your services by checking of <strong>Confirm</strong> or <strong>Decline</strong> on all listed services.
                            When finished, confirm your {(state?.fields || []).find((f: any) => f.key === document.entryType)?.title || startCase(document.entryType)} by clicking <strong>Approve &amp; Sign</strong>.</Message>}
                        </Fragment>
                      }

                      <MobileView>
                        <h4>Services</h4>
                      </MobileView>
                      <BrowserView>
                        <p>&nbsp;</p>
                        <h3>Services</h3>
                      </BrowserView>

                      <MobileView>
                        {document.services.filter((s: any) => s.isVisible).map((row: any) =>
                          <div key={row.guid}>
                            {['header', 'footer'].includes(row.service)
                              ? <h5>{row.workDescription}</h5>
                              : <div className="content p-5">
                                <div dangerouslySetInnerHTML={{ __html: row.workDescription.replace(/(?:\r\n|\r|\n)/g, '<br />') }} />
                                <p>{row.workCost}</p>
                                {(!isExpired && !excludeApproval()) &&
                                  <ButtonToolbar>
                                    <IconButton
                                      onClick={() => {
                                        document.approvalStatus === 'approved'
                                          ? window.alert('Services cannot be changed after signing')
                                          : handleUpdateService(row.guid, document.guid, 'confirmed')
                                      }
                                      }
                                      appearance="subtle"
                                      style={{ color: row.status === 'confirmed' ? '#5cb85c' : '#ccc' }}
                                      icon={<Icon as={MdCheckCircle} style={{ color: row.status === 'confirmed' ? '#5cb85c' : '#ccc' }} />}
                                    >Confirm</IconButton>

                                    <IconButton
                                      onClick={() => {
                                        document.approvalStatus === 'approved'
                                          ? window.alert('Services cannot be changed after signing')
                                          : handleUpdateService(row.guid, document.guid, 'declined')
                                      }
                                      }
                                      appearance="subtle"
                                      style={{ color: row.status === 'declined' ? '#d9534f' : '#ccc' }}
                                      icon={<Icon as={MdCancel} style={{ color: row.status === 'declined' ? '#d9534f' : '#ccc' }} />}
                                    >Decline</IconButton>
                                  </ButtonToolbar>
                                }
                              </div>
                            }
                          </div>
                        )}
                      </MobileView>
                      <BrowserView>
                        <div className="content">
                          <ResponsiveTable
                            autoHeight
                            wordWrap
                            data={document.services.filter((s: any) => s.isVisible)}
                            html5
                          >
                            <Column width={isTabletOrMobile ? 200 : 650}>
                              <HeaderCell>Description</HeaderCell>
                              <Cell>
                                {(row: any) => <div dangerouslySetInnerHTML={{ __html: (row?.workDescription || '').replace(/(?:\r\n|\r|\n)/g, '<br />') }} />}
                              </Cell>
                            </Column>

                            {!excludeApproval() &&
                              <Column>
                                <HeaderCell>Cost</HeaderCell>
                                <Cell dataKey="workCost"></Cell>
                              </Column>
                            }

                            {(!isExpired && !excludeApproval()) &&
                              <Column align="center">
                                <HeaderCell>Confirm</HeaderCell>
                                <Cell className="link-group">
                                  {(row: any) => ['header', 'footer'].includes(row.service)
                                    ? <Fragment />
                                    : <Whisper placement="bottom" trigger="hover" speaker={<Tooltip>Confirm</Tooltip>}>
                                      <IconButton
                                        onClick={() => {
                                          document.approvalStatus === 'approved'
                                            ? window.alert('Services cannot be changed after signing')
                                            : handleUpdateService(row.guid, document.guid, 'confirmed')
                                        }
                                        }
                                        appearance="subtle"
                                        icon={<Icon as={MdCheckCircle} style={{ color: row.status === 'confirmed' ? '#5cb85c' : '#ccc' }} />}
                                      />
                                    </Whisper>
                                  }
                                </Cell>
                              </Column>
                            }

                            {(!isExpired && !excludeApproval()) &&
                              <Column align="center">
                                <HeaderCell>Decline</HeaderCell>
                                <Cell className="link-group">
                                  {(row: any) => ['header', 'footer'].includes(row.service)
                                    ? <Fragment />
                                    :
                                    <Whisper placement="bottom" trigger="hover" speaker={<Tooltip>Decline</Tooltip>}>
                                      <IconButton
                                        onClick={() => {
                                          document.approvalStatus === 'approved'
                                            ? window.alert('Services cannot be changed after signing')
                                            : handleUpdateService(row.guid, document.guid, 'declined')
                                        }}
                                        appearance="subtle"
                                        icon={<Icon as={MdCancel} style={{ color: row.status === 'declined' ? '#d9534f' : '#ccc' }} />}
                                      />
                                    </Whisper>
                                  }
                                </Cell>
                              </Column>
                            }
                          </ResponsiveTable>
                        </div>
                      </BrowserView>

                      {IS_KALADI_PROPERTIES &&
                        <>
                          <MobileView>
                            <h4>Signatures</h4>
                          </MobileView>
                          <BrowserView>
                            <p>&nbsp;</p>
                            <h3>Signatures</h3>
                          </BrowserView>
                          <table className='html5'>
                            <thead>
                              <tr>
                                <th style={{ width: '20%' }}>Tenant</th>
                                <th>Signature</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(document.tenant.contacts || []).map((t: any) =>
                                <tr>
                                  <td>{t.name}</td>
                                  <td>
                                    {document?.tenantDateSigned?.[t.guid]
                                      ? <span>Signed on: {format(parseISO(document?.tenantDateSigned?.[t.guid]), FORMAT.SHORT_DAY_MONTH_TIME)}</span>
                                      : <Button
                                        size="xs"
                                        appearance='ghost'
                                        onClick={() => {
                                          setDocumentSigning(true);
                                          setTenantContactId(t.guid);
                                          setTenantId(document?.tenant?.guid);
                                        }}
                                      >Click Here to Sign</Button>
                                    }
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </>
                      }

                      {!isExpired &&
                        <div style={{ textAlign: 'center' }}>
                          {(!isCompleted() && !excludeApproval()) &&
                            <p className="mb-5 text-danger">
                              <strong>All service line items must Confirmed or Declined to Approve &amp; Sign the document</strong>
                            </p>}
                        </div>
                      }

                      {(documentView || '').includes('table') &&
                        <BrowserView>
                          <div style={{ marginTop: '24px' }}>
                            <h2>Terms & Conditions</h2>
                            <iframe
                              ref={iframeRef}
                              height={iframeHeight}
                              style={{ width: '100%', border: 0, overflow: 'hidden' }}
                              src={`https://${window.location.host}/api/documents/view/${token.data.resource}?access_token=${idToken}`}
                              onLoad={(obj: any) => {
                                setIframeHeight(iframeRef.current.contentWindow.document.body.scrollHeight + "px");
                              }}
                            />
                          </div>
                        </BrowserView>
                      }

                      {(IS_GREYROCK && document.entryType.includes('commercial')) &&
                        <div>
                          <MobileView>
                            <h4>Key Personel & Sub-Contractors</h4>
                            <Grid fluid>
                              {contacts.map((row: any, index: number) => <div style={{ marginTop: index === 0 ? 0 : '4px' }} key={`contact-${row.guid}`}>
                                <Row className='mt-4'>
                                  <Col xs={8} className='text-right'>Name:</Col>
                                  <Col xs={16}>
                                    <Input defaultValue={row.name} data-guid={row.guid} name="name" size="sm" onChange={handleUpdateContact} />
                                  </Col>
                                </Row>
                                <Row className='mt-4'>
                                  <Col xs={8} className='text-right'>Title & Department:</Col>
                                  <Col xs={16}>
                                    <Input defaultValue={row.phone} data-guid={row.guid} name="title" size="sm" onChange={handleUpdateContact} />
                                  </Col>
                                </Row>
                                <Row className='mt-4'>
                                  <Col xs={8} className='text-right'>Direct Phone #:</Col>
                                  <Col xs={16}>
                                    <Input defaultValue={row.email} data-guid={row.guid} name="phone" size="sm" onChange={handleUpdateContact} />
                                  </Col>
                                </Row>
                                <Row className='mt-4'>
                                  <Col xs={8} className='text-right'>Email Address:</Col>
                                  <Col xs={16}>
                                    <Input defaultValue={row.email} data-guid={row.guid} name="email" size="sm" onChange={handleUpdateContact} />
                                  </Col>
                                </Row>
                                <Row className='mt-4'>
                                  <Col xs={16} xsPush={8}>
                                    <Button appearance='link' size="sm" onClick={() => setContacts([...contacts, { guid: uuidv4() }])}><MdAdd /> Add Another</Button>
                                    {index > 0 && <Button appearance="link" style={{ color: COLOR.DANGER }} onClick={() => setContacts(contacts.filter((c: any) => c.guid !== row.guid))}><MdDelete /> Remove</Button>}
                                  </Col>
                                </Row>
                              </div>)}
                            </Grid>
                            <ButtonGroup className='mt-4 ml-16'>
                              <Button appearance='primary' size="sm" onClick={handleUpdateClientContacts}>Save</Button>
                            </ButtonGroup>
                          </MobileView>
                          <BrowserView>
                            <p>&nbsp;</p>
                            <h3>Key Personel & Sub-Contractors</h3>

                            <div>
                              <Grid fluid>
                                <Row>
                                  <Col md={5}><span className='font-medium'>Name</span></Col>
                                  <Col md={6}><span className='font-medium'>Title & Department</span></Col>
                                  <Col md={6}><span className='font-medium'>Direct Phone #</span></Col>
                                  <Col md={6}><span className='font-medium'>Email Address</span></Col>
                                </Row>
                                {contacts.map((row: any, index: number) => <Row style={{ marginTop: index === 0 ? 0 : '4px' }} key={`contact-${row.guid}`}>
                                  <Col md={5}>
                                    <Input defaultValue={row.name} data-guid={row.guid} name="name" size="sm" onChange={handleUpdateContact} />
                                  </Col>
                                  <Col md={6}>
                                    <Input defaultValue={row.phone} data-guid={row.guid} name="title" size="sm" onChange={handleUpdateContact} />
                                  </Col>
                                  <Col md={6}>
                                    <Input defaultValue={row.email} data-guid={row.guid} name="phone" size="sm" onChange={handleUpdateContact} />
                                  </Col>
                                  <Col md={6}>
                                    <Input defaultValue={row.email} data-guid={row.guid} name="email" size="sm" onChange={handleUpdateContact} />
                                  </Col>
                                  <Col md={1}>
                                    {index > 0 && <IconButton appearance="link" icon={<MdDelete />} style={{ color: COLOR.DANGER }} onClick={() => setContacts(contacts.filter((c: any) => c.guid !== row.guid))} />}
                                  </Col>
                                </Row>)}
                                <ButtonGroup className='mt-4'>
                                  <Button appearance='primary' size="sm" onClick={handleUpdateClientContacts}>Save</Button>
                                  <Button appearance='link' size="sm" onClick={() => setContacts([...contacts, { guid: uuidv4() }])}><MdAdd /> Add Another</Button>
                                </ButtonGroup>
                              </Grid>
                            </div>
                            <p>&nbsp;</p>
                          </BrowserView>
                        </div>
                      }


                      {/* 
                    {!isExpired &&
                      <div style={{ textAlign: 'center' }}>
                        {(!isCompleted() && !IS_KALADI_KITCHENS) &&
                          <p className="mb-5 text-danger">
                            <strong>All service line items must Confirmed or Declined to Approve &amp; Sign the document</strong>
                          </p>}
                      </div>
                    } */}

                      {!isExpired &&
                        <div style={{ textAlign: 'center', marginTop: '24px' }}>
                          <ButtonGroup>
                            {(!document.clientDateSigned && !IS_KALADI_PROPERTIES) &&
                              <Button
                                appearance="primary"
                                disabled={(!isCompleted() && !excludeApproval())}
                                onClick={() => setDocumentSigning(true)} size="md">Approve &amp; Sign</Button>
                            }

                            <Button appearance={(document.clientDateSigned || IS_KALADI_PROPERTIES) ? 'primary' : 'subtle'} size="md" onClick={() => {
                              if (idToken) {
                                window.location.href = `https://${window.location.host}/api/documents/download/${token.data.resource}?access_token=${idToken}`
                              } else {
                                showError(new Error('Customer token not found'));
                              }
                            }}>Download {(state?.fields || []).find((f: any) => f.key === document.entryType)?.title || startCase(document.entryType)}</Button>
                          </ButtonGroup>
                          <p>&nbsp;</p>
                        </div>
                      }

                      {document.attachments.filter((a: any) => a.visibility === 'public').length > 0 &&
                        <>
                          <MobileView>
                            <h4>Attachments</h4>
                          </MobileView>
                          <BrowserView>
                            <h2>Attachments</h2>
                          </BrowserView>

                          <div className="content">
                            <Table
                              autoHeight={true}
                              data={document.attachments.filter((a: any) => a.visibility === 'public')}
                            >
                              <Column flexGrow={2}>
                                <HeaderCell>Filename</HeaderCell>
                                <Cell dataKey="fileName"></Cell>
                              </Column>
                              <Column align="center">
                                <HeaderCell>Download</HeaderCell>
                                <Cell className="link-group">
                                  {(row: any) => <IconButton
                                    onClick={() => {
                                      if (idToken) {
                                        window.location.href = `https://${window.location.host}/api/attachments/download/${row.guid}?access_token=${idToken}`
                                      } else {
                                        showError(new Error('Customer token not found'));
                                      }
                                    }}
                                    appearance="link"
                                    icon={<Icon as={MdDownload} />}
                                  />}
                                </Cell>
                              </Column>
                            </Table>
                          </div>
                        </>
                      }
                    </Col>
                  </Row>
                </Grid>
                : <Message type='error'>Document not found. Please contact our office for new link.</Message>
              }
            </>
          }

          <ClientRepresentativeSignature
            signatureType={IS_KALADI_PROPERTIES ? 'tenant' : 'client'}
            tenantId={tenantId}
            tenantContactId={tenantContactId}
            show={documentSigning}
            document={document}
            onHide={() => {
              setDocumentSigning(false);
              setTenantId(undefined);
              setTenantContactId(undefined);
            }}
            onApproveDocument={(result: any) => {
              setDocument(result);
              setDocumentSigning(false);
              setTenantId(undefined);
              setTenantContactId(undefined);
            }}
          />
        </Fragment>
      }
    </div>
  );
}

export default PortalDocumentView;
